import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GetWSMgr } from "@/Utils/StateLib";
import {
  isCanvasRoute,
  isResourceRoute,
  needsCreateParamForEdit,
  appRoutes
} from "@coreapp/constants";
import { LocationSource } from "@coreapp/CoreApp/LocationSource";
import {
  routePaths,
  CHART_QUERY_PARAM_KEYS,
  NON_CANVAS_CARRY_KEYS,
  type ChartQueryParams,
  type ChartMode
} from "@/appComponents/routes";

type NavOptions = { replace?: boolean };

function buildUrl(basePath: string, params?: Record<string, string | boolean | undefined>): string {
  if (!params) return basePath;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === true) sp.set(k, "true");
    else if (v != null && v !== false) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `${basePath}?${s}` : basePath;
}

export const useAppNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const toChart = useCallback(
    (chartId: string, params?: ChartQueryParams) => {
      navigate(buildUrl(routePaths.base(chartId), params));
    },
    [navigate]
  );

  const toCustomChart = useCallback(
    (directory: string, file: string, params?: ChartQueryParams) => {
      navigate(buildUrl(routePaths.open(directory, file), params));
    },
    [navigate]
  );

  const toPlan = useCallback(
    (planId: string | number, params?: ChartQueryParams) => {
      // Default the sidebar tool to "dashboard" on planning entry — same
      // invariant DoPlanningMode's comment calls out. Without a default,
      // RightSidebarProvider keeps the previous route's ?tool= (filters,
      // highlights, etc.) active and the planning-exclusive tabs
      // (Dashboard, Activity, TalentPool) render empty. Callers that want
      // a specific tool can still override via the params arg.
      const withDefaults: ChartQueryParams = { tool: "dashboard", ...params };
      navigate(buildUrl(routePaths.plan(planId), withDefaults));
    },
    [navigate]
  );

  /** Snapshot of canvas-context passenger params off the current URL.
   *  Settings, details, and box-layout navigation read passengers through
   *  this helper so adding a new canvas key in CHART_QUERY_PARAM_KEYS
   *  propagates automatically. Pass a narrower `keys` (e.g. NON_CANVAS_
   *  CARRY_KEYS) when navigating to a non-canvas surface — settings and
   *  details have no use for transient view state like ?tool= or
   *  ?display= and including them pollutes the URL (and worse, ?tool=
   *  background flips InBackgroundEditorMode true on settings routes). */
  const getCanvasContextParams = useCallback(
    (
      keys: ReadonlyArray<keyof ChartQueryParams> = CHART_QUERY_PARAM_KEYS
    ): Record<string, string> => {
      const current = new URLSearchParams(location.search);
      const carry: Record<string, string> = {};
      for (const key of keys) {
        const val = current.get(key);
        if (val !== null) carry[key] = val;
      }
      return carry;
    },
    [location.search]
  );

  const toBoxLayout = useCallback(
    (set: string, id: string, params?: ChartQueryParams | Record<string, string>) => {
      navigate(buildUrl(routePaths.boxlayout(set, id), params));
    },
    [navigate]
  );

  /** Navigate into or within settings, preserving canvas context params.
   *  From canvas: grabs mode, create, tool, view, etc. from the current URL.
   *  Within settings: preserves existing search (already carries them).
   *  Builds /settings[/segment1[/segment2[/...]]] — caller never types the
   *  /settings prefix. One method, all settings navigation goes through it.
   *
   *  Pass `{ replace: true }` as the trailing arg for tab switches and URL
   *  canonicalization so internal Settings writes don't pollute history —
   *  Cancel and browser-back must always exit Settings, not cycle tabs.
   *
   *  nav.navigateSettings()                          → /settings
   *  nav.navigateSettings("chart", chartId)          → /settings/chart/{id}
   *  nav.navigateSettings("chart", chartId, "profile") → /settings/chart/{id}/profile
   *  nav.navigateSettings("account")                 → /settings/account
   *  nav.navigateSettings("plan", planId, "general") → /settings/plan/{id}/general
   *  nav.navigateSettings("chart", chartId, "profile", { replace: true })
   */
  const navigateSettings = useCallback(
    (...args: Array<string | NavOptions>) => {
      const last = args[args.length - 1];
      const hasOptions = typeof last === "object" && last !== null;
      const options = hasOptions ? (last as NavOptions) : undefined;
      const segments = (hasOptions ? args.slice(0, -1) : args) as string[];
      const path =
        "/settings" + (segments.length > 0 ? "/" + segments.filter(Boolean).join("/") : "");
      navigate(buildUrl(path, getCanvasContextParams(NON_CANVAS_CARRY_KEYS)), options);
    },
    [navigate, getCanvasContextParams]
  );

  const toChartSettings = useCallback(
    (chartId?: string) => navigateSettings("chart", chartId ?? ""),
    [navigateSettings]
  );

  const toAccountSettings = useCallback(() => navigateSettings("account"), [navigateSettings]);

  const toPlanSettings = useCallback(
    (planId?: string) => navigateSettings("plan", planId ?? ""),
    [navigateSettings]
  );

  /** Navigate into or within details pages, preserving canvas context.
   *  Same pattern as navigateSettings — one method, all detail pages.
   *  nav.navigateDetails()          → /details
   *  nav.navigateDetails("chart")   → /details/chart
   *  nav.navigateDetails("user")    → /details/user
   *  nav.navigateDetails("account") → /details/account
   */
  const navigateDetails = useCallback(
    (...segments: string[]) => {
      const path =
        "/details" + (segments.length > 0 ? "/" + segments.filter(Boolean).join("/") : "");
      navigate(buildUrl(path, getCanvasContextParams(NON_CANVAS_CARRY_KEYS)));
    },
    [navigate, getCanvasContextParams]
  );

  const toDetails = useCallback(
    (section?: string) => navigateDetails(section ?? ""),
    [navigateDetails]
  );

  const toCurrentResource = useCallback(
    (params?: ChartQueryParams, options?: NavOptions) => {
      const wsMgr = GetWSMgr();
      const doc = wsMgr?.GetDoc();
      if (!doc || !wsMgr) return;

      const docMgr = wsMgr.GetDocMgr();
      const planMgr = wsMgr.GetPlanMgr?.();

      // planID 0 is valid (first plan on a fresh org) — test != null, not truthiness.
      if (planMgr?.GetPlanID() != null) {
        navigate(buildUrl(routePaths.plan(planMgr.GetPlanID()), params), options);
      } else if (doc.IsServerChart() && doc.GetChartID()) {
        navigate(buildUrl(routePaths.base(String(doc.GetChartID())), params), options);
      } else {
        const fileName = docMgr.GetFileName(true);
        const directory = docMgr.GetFileDirectory();
        if (fileName && directory) {
          navigate(buildUrl(routePaths.open(directory, fileName), params), options);
        } else if (doc.GetChartID()) {
          navigate(buildUrl(routePaths.base(String(doc.GetChartID())), params), options);
        }
      }
    },
    [navigate]
  );

  const setMode = useCallback(
    (mode: ChartMode, options?: { replace?: boolean }) => {
      // Box layout mode is encoded in the path, not ?mode= — navigate to the
      // current resource route instead of modifying search params.
      if (location.pathname.startsWith("/boxlayout/")) {
        toCurrentResource(mode !== "chart" ? { mode } : undefined);
      } else if (isCanvasRoute(location.pathname)) {
        // Resource routes (/base, /open, /plan) honor ?mode=; legacy canvas
        // routes (/, /chart, /edit, /planning) encode mode in the PATH, where
        // GetUIMode ignores ?mode= (QUIConfig legacy branch). On a legacy route
        // we must switch the path segment — mirroring coreapp's own mode→path
        // map (QXWSMgr.modePath / GetRouteForMode → appRoutes) — otherwise the
        // mode transition silently no-ops (e.g. an unsaved builder chart pinned
        // to /chart can never enter edit mode).
        const legacyModePath: Partial<Record<ChartMode, string>> = {
          chart: appRoutes.chart,
          edit: appRoutes.edit
        };
        const targetLegacyPath = isResourceRoute(location.pathname)
          ? undefined
          : legacyModePath[mode];

        const params = new URLSearchParams(location.search);
        if (params.get("tool") === "background") {
          params.delete("tool");
        }

        if (targetLegacyPath) {
          // Mode is carried by the path here — drop any query-encoded mode/create.
          params.delete("mode");
          params.delete("create");
          const search = params.toString();
          navigate(search ? `${targetLegacyPath}?${search}` : targetLegacyPath, options);
        } else {
          if (mode === "chart") {
            params.delete("mode");
            params.delete("create");
          } else {
            params.set("mode", mode);
            if (mode === "edit") {
              const [, segment = ""] = location.pathname.split("/");
              if (needsCreateParamForEdit(segment, GetWSMgr())) params.set("create", "true");
            }
          }
          const search = params.toString();
          navigate(search ? `${location.pathname}?${search}` : location.pathname, options);
        }
      } else {
        toCurrentResource(mode !== "chart" ? { mode } : undefined);
      }
    },
    [navigate, location.pathname, location.search, toCurrentResource]
  );

  const goHome = useCallback(
    (params?: ChartQueryParams) => {
      toCurrentResource(params);
    },
    [toCurrentResource]
  );

  const setSearchParams = useCallback(
    (
      updater:
        ((prev: URLSearchParams) => URLSearchParams) | URLSearchParams | Record<string, string>,
      options?: NavOptions
    ) => {
      const current = new URLSearchParams(LocationSource.getSearch());
      const next = typeof updater === "function" ? updater(current) : new URLSearchParams(updater);
      const search = next.toString();
      navigate({ search: search ? "?" + search : "" }, options);
    },
    [navigate]
  );

  return {
    toChart,
    toCustomChart,
    toPlan,
    toBoxLayout,
    toChartSettings,
    toAccountSettings,
    toPlanSettings,
    toDetails,
    toCurrentResource,
    setMode,
    goHome,
    navigateSettings,
    navigateDetails,
    getCanvasContextParams,
    setSearchParams
  };
};
